import { useState } from 'react';
import type { Discussion } from '../page';

interface DiscussionCardProps {
  discussion: Discussion;
  viewMode: 'grid' | 'list';
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onDuplicate: () => void;
}

const colorClasses = {
  emerald: 'bg-emerald-50 border-emerald-200 hover:border-emerald-300',
  blue: 'bg-blue-50 border-blue-200 hover:border-blue-300',
  purple: 'bg-purple-50 border-purple-200 hover:border-purple-300',
  orange: 'bg-orange-50 border-orange-200 hover:border-orange-300',
  teal: 'bg-teal-50 border-teal-200 hover:border-teal-300',
  green: 'bg-green-50 border-green-200 hover:border-green-300',
  pink: 'bg-pink-50 border-pink-200 hover:border-pink-300',
  yellow: 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
};

const categoryIcons = {
  Strategy: 'ri-lightbulb-line',
  Product: 'ri-product-hunt-line',
  Meeting: 'ri-team-line',
  Feedback: 'ri-feedback-line',
  Business: 'ri-briefcase-line',
  Sustainability: 'ri-leaf-line'
};

export default function DiscussionCard({
  discussion,
  viewMode,
  onEdit,
  onDelete,
  onTogglePin,
  onDuplicate
}: DiscussionCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const colorClass = colorClasses[discussion.color as keyof typeof colorClasses] || colorClasses.emerald;
  const categoryIcon = categoryIcons[discussion.category as keyof typeof categoryIcons] || 'ri-file-text-line';

  const truncateContent = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (viewMode === 'list') {
    return (
      <div className={`${colorClass} border-2 rounded-lg p-4 transition-all cursor-pointer relative group`}>
        <div className="flex items-start gap-4">
          {/* Pin Indicator */}
          {discussion.isPinned && (
            <i className="ri-pushpin-fill w-5 h-5 flex items-center justify-center text-gray-600 flex-shrink-0"></i>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <h3 className="font-semibold text-gray-900 text-lg">{discussion.title}</h3>
              
              {/* Actions */}
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={onTogglePin}
                  className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
                  title={discussion.isPinned ? 'Unpin' : 'Pin'}
                >
                  <i className={`${discussion.isPinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'} w-4 h-4 flex items-center justify-center text-gray-600`}></i>
                </button>
                <button
                  onClick={onEdit}
                  className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
                  title="Edit"
                >
                  <i className="ri-edit-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
                  >
                    <i className="ri-more-2-fill w-4 h-4 flex items-center justify-center text-gray-600"></i>
                  </button>
                  {showMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowMenu(false)}
                      ></div>
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px] z-20">
                        <button
                          onClick={() => {
                            onDuplicate();
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
                        >
                          <i className="ri-file-copy-line w-4 h-4 flex items-center justify-center"></i>
                          Duplicate
                        </button>
                        <button
                          onClick={() => {
                            onDelete();
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <p className="text-gray-700 text-sm mb-3 line-clamp-2">
              {discussion.content}
            </p>

            <div className="flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white/50 px-2.5 py-1 rounded-full">
                <i className={`${categoryIcon} w-3.5 h-3.5 flex items-center justify-center`}></i>
                {discussion.category}
              </span>
              
              {discussion.tags.map(tag => (
                <span key={tag} className="text-xs text-gray-600 bg-white/50 px-2.5 py-1 rounded-full">
                  #{tag}
                </span>
              ))}

              <span className="text-xs text-gray-500 ml-auto">
                Updated {new Date(discussion.updatedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${colorClass} border-2 rounded-lg p-5 transition-all cursor-pointer relative group`}>
      {/* Pin Indicator */}
      {discussion.isPinned && (
        <i className="ri-pushpin-fill w-5 h-5 flex items-center justify-center absolute top-3 right-3 text-gray-600"></i>
      )}

      {/* Header */}
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 text-lg mb-2 pr-8">{discussion.title}</h3>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white/50 px-2.5 py-1 rounded-full">
          <i className={`${categoryIcon} w-3.5 h-3.5 flex items-center justify-center`}></i>
          {discussion.category}
        </span>
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-gray-700 text-sm whitespace-pre-wrap">
          {isExpanded ? discussion.content : truncateContent(discussion.content, 150)}
        </p>
        {discussion.content.length > 150 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-emerald-600 text-sm font-medium mt-2 hover:text-emerald-700 cursor-pointer"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Tags */}
      {discussion.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {discussion.tags.map(tag => (
            <span key={tag} className="text-xs text-gray-600 bg-white/50 px-2.5 py-1 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-200">
        <span>Updated {new Date(discussion.updatedAt).toLocaleDateString()}</span>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onTogglePin}
            className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
            title={discussion.isPinned ? 'Unpin' : 'Pin'}
          >
            <i className={`${discussion.isPinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'} w-4 h-4 flex items-center justify-center text-gray-600`}></i>
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
            title="Edit"
          >
            <i className="ri-edit-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
          </button>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 hover:bg-white/50 rounded transition-colors cursor-pointer"
            >
              <i className="ri-more-2-fill w-4 h-4 flex items-center justify-center text-gray-600"></i>
            </button>
            {showMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowMenu(false)}
                ></div>
                <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px] z-20">
                  <button
                    onClick={() => {
                      onDuplicate();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-file-copy-line w-4 h-4 flex items-center justify-center"></i>
                    Duplicate
                  </button>
                  <button
                    onClick={() => {
                      onDelete();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
